import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faEdit, faLock, faUnlink, faCheck, faTimes, faGraduationCap, faTrash, faCreditCard, faSpinner } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import EditProfileModal from '@/components/auth/EditProfileModal';
import ChangePasswordModal from '@/components/auth/ChangePasswordModal';
import EditSCNConfigModal from '@/components/auth/EditSCNConfigModal';
import DisconnectSCNModal from '@/components/auth/DisconnectSCNModal';
import DeleteAccountModal from '@/components/auth/DeleteAccountModal';
import type { User, Course, Subscription } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import { showToast } from '@/utils/toasts';
import { formatCurrencyCentValue } from '@/utils/helpers';
import { PAGINATION } from '@/config';
import { JOURNAL_ROUTES } from '@/constants/routes';

export default function UserSettings() {
  const { user, isAdmin, isEmployee, updateUser } = useUser();
  const navigate = useNavigate();
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEditSCNConfigModalOpen, setIsEditSCNConfigModalOpen] = useState(false);
  const [isDisconnectConfirmModalOpen, setIsDisconnectConfirmModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [stripeLink, setStripeLink] = useState<string | null>(null);
  const [isLoadingStripeLink, setIsLoadingStripeLink] = useState(false);

  useEffect(() => {
    fetchCourses();
    if (user !== null) {
      fetchSubscriptions();
    }
  }, [user]);

  const fetchCourses = async () => {
    setIsLoadingCourses(true);
    try {
      const response = await APIRoutes.listCourses({ limit: PAGINATION.FETCH_ALL_LIMIT });
      setCourses(response.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const fetchSubscriptions = async () => {
    if (user === null) return;
    try {
      const response = await APIRoutes.getUserSubscriptions(user.id);
      setSubscriptions(response.subscriptions);

      // Fetch stripe link if there are subscriptions
      if (response.subscriptions.length > 0) {
        fetchStripeLink();
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const fetchStripeLink = async () => {
    if (user === null) return;
    setIsLoadingStripeLink(true);
    try {
      const response = await APIRoutes.getStripeLink(user.id);
      setStripeLink(response.link);
    } catch (error) {
      console.error('Error fetching stripe link:', error);
    } finally {
      setIsLoadingStripeLink(false);
    }
  };

  // Get user initials
  const getInitials = () => {
    if (!user) return 'U';
    const firstInitial = user.firstName?.charAt(0) || '';
    const lastInitial = user.lastName?.charAt(0) || '';
    return (firstInitial + lastInitial).toUpperCase();
  };

  const handleUserUpdated = (updatedUser: User) => {
    updateUser(updatedUser);
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const updatedUser = await APIRoutes.updateCurrentUser({
        scnUID: '',
        scnToken: ''
      });

      updateUser(updatedUser);
      showToast('SCN-Verbindung erfolgreich getrennt', 'success');
      setIsDisconnectConfirmModalOpen(false);
    } catch (error) {
      console.error('Error disconnecting SCN:', error);
      showToast('Fehler beim Trennen der SCN-Verbindung', 'error');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setIsDeletingAccount(true);
    try {
      await APIRoutes.deleteUser(user.id);

      showToast('Ihr Account wurde erfolgreich gelöscht', 'success');
      setIsDeleteAccountModalOpen(false);

      // Logout and redirect to login
      APIRoutes.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast('Fehler beim Löschen des Accounts', 'error');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleEmailOptOutToggle = async (type: 'courseMailSequence' | 'videoPublished' | 'recommendationMails') => {
    if (!user) return;

    try {
      const updatedOptOut = {
        ...user.emailOptOut,
        [type]: !user.emailOptOut[type]
      };

      const updatedUser = await APIRoutes.updateCurrentUser({
        emailOptOut: updatedOptOut
      });

      updateUser(updatedUser);
      showToast('E-Mail-Einstellungen aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating email preferences:', error);
      showToast('Fehler beim Aktualisieren der E-Mail-Einstellungen', 'error');
    }
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
          Benutzereinstellungen
        </h1>

        {/* User Profile Section */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-3xl font-extrabold text-white">
                {getInitials()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {user ? `${user.firstName} ${user.lastName}` : 'User'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {isAdmin ? 'Administrator' : isEmployee ? 'Mitarbeiter' : 'Benutzer'}
              </p>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Account Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <FontAwesomeIcon icon={faCog} className="w-5 h-5 mr-2" />
                Account-Einstellungen
              </h3>
              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Profil bearbeiten
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Name und Telefonnummer ändern
                    </p>
                  </div>
                  <button
                    onClick={() => setIsEditProfileModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Passwort ändern
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sicherheitspasswort aktualisieren
                    </p>
                  </div>
                  <button
                    onClick={() => setIsChangePasswordModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faLock} className="w-4 h-4 mr-2" />
                    Ändern
                  </button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        SCN konfigurieren
                      </h4>
                      {user?.scnUID && (
                        <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
                          Verbunden
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Simple Cloud Notifications Zugangsdaten verwalten
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    {user?.scnUID && (
                      <button
                        onClick={() => setIsDisconnectConfirmModalOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faUnlink} className="w-4 h-4 mr-2" />
                        Trennen
                      </button>
                    )}
                    <button
                      onClick={() => setIsEditSCNConfigModalOpen(true)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      <FontAwesomeIcon icon={faEdit} className="w-4 h-4 mr-2" />
                      Konfigurieren
                    </button>
                  </div>
                </div>

                {subscriptions.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Zahlungen verwalten
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Abonnements und Zahlungsmethoden verwalten
                      </p>
                    </div>
                    <a
                      href={stripeLink ?? "https://billing.stripe.com/p/login/8x228s5PU8oRa9DcAv1VK00"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isLoadingStripeLink
                          ? 'opacity-50 cursor-not-allowed pointer-events-none'
                          : 'hover:bg-blue-50 dark:hover:bg-blue-900 cursor-pointer'
                      }`}
                      aria-disabled={isLoadingStripeLink}
                    >
                      {isLoadingStripeLink ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4 mr-2" />
                      )}
                      Verwalten
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* User Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Benutzerinformationen
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Name
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {user ? `${user.firstName} ${user.lastName}` : 'Nicht verfügbar'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    E-Mail
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {user?.email || 'Nicht verfügbar'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Telefon
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {user?.phone || 'Nicht verfügbar'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Berechtigungsgruppe
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {user?.permissionGroup || 'Nicht verfügbar'}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Access */}
            {user && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Meine Features
                </h3>
                <div className="space-y-3">
                  {/* Core Recommendations */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.coreRecommendations ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.coreRecommendations ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Core-Tradingideen
                        </h4>
                        {user.featureConfig.coreRecommendations && user.featureConfig.coreRecommendationStrikeLimit !== null && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Limit: {formatCurrencyCentValue(user.featureConfig.coreRecommendationStrikeLimit, 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Earnings Recommendations */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.earningRecommendations ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.earningRecommendations ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Earnings-Tradingideen
                        </h4>
                        {user.featureConfig.earningRecommendations && user.featureConfig.earningRecommendationStrikeLimit !== null && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Limit: {formatCurrencyCentValue(user.featureConfig.earningRecommendationStrikeLimit, 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Swing Trade Recommendations */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.swingTradeRecommendations ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.swingTradeRecommendations ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Swing-Tradingideen
                        </h4>
                        {user.featureConfig.swingTradeRecommendations && user.featureConfig.swingTradeRecommendationPriceLimit !== null && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Startpreis-Limit: {formatCurrencyCentValue(user.featureConfig.swingTradeRecommendationPriceLimit, 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Long Term Recommendations */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.longTermRecommendations ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.longTermRecommendations ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Langzeit-Tradingideen
                        </h4>
                        {user.featureConfig.longTermRecommendations && user.featureConfig.longTermRecommendationPriceLimit !== null && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Startpreis-Limit: {formatCurrencyCentValue(user.featureConfig.longTermRecommendationPriceLimit, 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Boost Recommendations - Only visible to community members */}
                  {user.featureConfig.communityMember && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={user.featureConfig.boostRecommendations ? faCheck : faTimes}
                          className={`w-5 h-5 mr-3 ${user.featureConfig.boostRecommendations ? 'text-green-600' : 'text-red-600'}`}
                        />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            Boost-Tradingideen
                          </h4>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Trading-Buddy - Only visible to users with the chatbot feature */}
                  {user.featureConfig.chatbot && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="w-5 h-5 mr-3 text-green-600"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            Trading-Buddy
                          </h4>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Video-Finder */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.videoSearch ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.videoSearch ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Video-Finder
                        </h4>
                      </div>
                    </div>
                  </div>

                  {/* Video Chatbot - Currently hidden
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={user.featureConfig.videoChatbot ? faCheck : faTimes}
                        className={`w-5 h-5 mr-3 ${user.featureConfig.videoChatbot ? 'text-green-600' : 'text-red-600'}`}
                      />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Video-Chatbot
                        </h4>
                      </div>
                    </div>
                  </div>
                  */}

                  {user.featureConfig.journal && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="w-5 h-5 mr-3 text-green-600"
                        />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            Trading Journal
                          </h4>
                        </div>
                      </div>
                      <Link
                        to={JOURNAL_ROUTES.SETTINGS}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Einstellungen
                      </Link>
                    </div>
                  )}

                  {/* All Courses - Only visible to community members */}
                  {user.featureConfig.communityMember && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={user.featureConfig.allCourses ? faCheck : faTimes}
                          className={`w-5 h-5 mr-3 ${user.featureConfig.allCourses ? 'text-green-600' : 'text-red-600'}`}
                        />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                            <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-2" />
                            Alle Kurse
                          </h4>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Individual Courses */}
                  {!isLoadingCourses && !user.featureConfig.allCourses && courses.some(c => user.featureConfig.courses[c.id]) && (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                        <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-2" />
                        Einzelne Kurse
                      </h4>
                      <div className="space-y-2 ml-6">
                        {courses
                          .filter(course => user.featureConfig.courses[course.id])
                          .map(course => (
                            <div key={course.id} className="flex items-center">
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="w-4 h-4 mr-2 text-green-600"
                              />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {course.title}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Upgrade Button - nur für Nicht-CommunityMember */}
                {!user.featureConfig.communityMember && (
                  <Link
                    to="/pricing"
                    className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    Zum Upgrade-Bereich
                  </Link>
                )}
              </div>
            )}

            {/* Email Preferences */}
            {user && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  E-Mail-Einstellungen
                </h3>
                <div className="space-y-3">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Kurs E-Mail-Sequenz
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Automatische E-Mails zu Kurs-Fortschritten und -Inhalten
                        </p>
                      </div>
                      <button
                        onClick={() => handleEmailOptOutToggle('courseMailSequence')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                          user.emailOptOut.courseMailSequence
                            ? 'bg-gray-400 dark:bg-gray-600'
                            : 'bg-blue-600 dark:bg-blue-500'
                        }`}
                        aria-label={user.emailOptOut.courseMailSequence ? 'E-Mails aktivieren' : 'E-Mails deaktivieren'}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          user.emailOptOut.courseMailSequence ? 'translate-x-1' : 'translate-x-6'
                        }`} />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Neue Videos
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Benachrichtigungen wenn neue Videos veröffentlicht werden
                        </p>
                      </div>
                      <button
                        onClick={() => handleEmailOptOutToggle('videoPublished')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                          user.emailOptOut.videoPublished
                            ? 'bg-gray-400 dark:bg-gray-600'
                            : 'bg-blue-600 dark:bg-blue-500'
                        }`}
                        aria-label={user.emailOptOut.videoPublished ? 'E-Mails aktivieren' : 'E-Mails deaktivieren'}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          user.emailOptOut.videoPublished ? 'translate-x-1' : 'translate-x-6'
                        }`} />
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Empfehlungs-E-Mails
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          E-Mails bei neuen HandelsTradingideen
                        </p>
                      </div>
                      <button
                        onClick={() => handleEmailOptOutToggle('recommendationMails')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                          user.emailOptOut.recommendationMails
                            ? 'bg-gray-400 dark:bg-gray-600'
                            : 'bg-blue-600 dark:bg-blue-500'
                        }`}
                        aria-label={user.emailOptOut.recommendationMails ? 'E-Mails aktivieren' : 'E-Mails deaktivieren'}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          user.emailOptOut.recommendationMails ? 'translate-x-1' : 'translate-x-6'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Danger Zone */}
            <div className="border-t border-red-200 dark:border-red-800 pt-6 mt-6">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-red-900 dark:text-red-100">
                      Account dauerhaft löschen
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      Löschen Sie Ihren Account und alle zugehörigen Daten (DSGVO)
                    </p>
                  </div>
                  <button
                    onClick={() => setIsDeleteAccountModalOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
                    Account löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {user && (
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          onClose={() => setIsEditProfileModalOpen(false)}
          user={user}
          onUserUpdated={handleUserUpdated}
        />
      )}

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      {user && (
        <EditSCNConfigModal
          isOpen={isEditSCNConfigModalOpen}
          onClose={() => setIsEditSCNConfigModalOpen(false)}
          user={user}
          onUserUpdated={handleUserUpdated}
        />
      )}

      <DisconnectSCNModal
        isOpen={isDisconnectConfirmModalOpen}
        onClose={() => setIsDisconnectConfirmModalOpen(false)}
        onConfirm={handleDisconnect}
        isLoading={isDisconnecting}
      />

      <DeleteAccountModal
        isOpen={isDeleteAccountModalOpen}
        onClose={() => setIsDeleteAccountModalOpen(false)}
        onConfirm={handleDeleteAccount}
        isLoading={isDeletingAccount}
      />
    </>
  );
}
